import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getMe, getTaskPlatformStatus, getTaskInbox, getTaskOutbox,
  createTask, syncTaskAssignees, TASK_PRIORITY_LABEL, myVoteRounds, pendingVoteCount,
  VOTE_STATUS_LABEL, VOTE_PHASE_LABEL,
  type MeResponse, type MyVoteRound,
} from '../lib/api';
import TaskCard from '../components/TaskCard';
import AssigneePicker, { type Assignee } from '../components/AssigneePicker';
import { VoteBallotForm, VoteNominationForm, VoterChips, CandidateRow } from '../components/VotePanel';
import { EmptyState } from '../components/ui/EmptyState';

/** My Tasks (§11) — internal staff delegate tasks (Outbox) and act on tasks assigned to them (Inbox),
 *  backed by the platform task API. Degrades to a banner when the task API isn't configured yet. */
export default function MyTasks() {
  const qc = useQueryClient();
  const { data: me } = useQuery<MeResponse>({ queryKey: ['me'], queryFn: getMe });
  const isInternal = me?.account?.subject_type === 'staff';
  const [tab, setTab] = useState<'inbox' | 'outbox'>('inbox');

  const status = useQuery({ queryKey: ['task-status'], queryFn: getTaskPlatformStatus, enabled: isInternal });
  // 定标投票 (§6): every listed voter owes a ballot before the tender can invite anyone. THIS is where
  // 项目经理 votes — the compare tab is closed to them (pricing lockdown 0f3f0c7), this payload is price-free.
  // F4: rounds move while this page sits open (someone else votes, the phase flips) — keep it live.
  const votes = useQuery({ queryKey: ['my-vote-rounds'], queryFn: myVoteRounds, enabled: isInternal, refetchInterval: 15000 });
  const inbox = useQuery({ queryKey: ['tasks', 'inbox'], queryFn: getTaskInbox, enabled: isInternal });
  const outbox = useQuery({ queryKey: ['tasks', 'outbox'], queryFn: getTaskOutbox, enabled: isInternal });

  if (!me?.authenticated) return <div className="card">请先登录 / please log in.</div>;
  if (!isInternal) return <div className="card">此页仅内部员工可见 / internal staff only.</div>;

  const list = tab === 'inbox' ? (inbox.data ?? []) : (outbox.data ?? []);
  const refresh = () => qc.invalidateQueries({ queryKey: ['tasks'] });

  // .tasks-page also reserves, on a phone, the bottom strip the floating FAB column sits over
  return (
    <div className="tasks-page">
      <div className="row-wrap" style={{ alignItems: 'center', gap: 12 }}>
        <h2 style={{ marginRight: 'auto' }}>我的任务 / My Tasks</h2>
        {me.account?.role === 'gm' && status.data?.configured && <SyncButton onSynced={() => qc.invalidateQueries({ queryKey: ['task-assignees'] })} />}
      </div>
      <PendingVotes rounds={votes.data?.rounds ?? []} isError={votes.isError} onRetry={() => votes.refetch()} />
      <DelegateForm configured={!!status.data?.configured} onCreated={refresh} />

      <div className="row-wrap tab-row" style={{ gap: 8, margin: '12px 0' }}>
        <button className={`tab${tab === 'inbox' ? ' active' : ''}`} onClick={() => setTab('inbox')}>
          📥 收件箱 / Inbox ({inbox.data?.length ?? 0})
        </button>
        <button className={`tab${tab === 'outbox' ? ' active' : ''}`} onClick={() => setTab('outbox')}>
          📤 已派发 / Outbox ({outbox.data?.length ?? 0})
        </button>
      </div>

      {list.length === 0 && <EmptyState title="暂无任务 / Nothing here" hint={tab === 'inbox' ? '有人派任务给您时会出现在这里。 / Tasks assigned to you land here.' : '您派发的任务会出现在这里。 / Tasks you delegate land here.'} />}
      {list.map((t) => <TaskCard key={t.id} t={t} box={tab} onChanged={refresh} />)}
    </div>
  );
}

/**
 * 定标投票 — everything this round owes me, and everything it decided.
 *
 * Three kinds ride in the same list (改改 fb_85f73594 + V5): a 推荐 I still owe, a 表决 I still owe,
 * and — read-only — a round I sat on that COMPLETED in the last 14 days, because until now the panel
 * had no surface at all for the outcome they were made to vote on. The tender cannot invite anyone
 * until every vote is in ("5张票必须投完，否则进行不下去"), so this sits at the top of 我的任务.
 * Zero price data by construction: 项目经理 acts from here.
 */
function PendingVotes({ rounds, isError, onRetry }: { rounds: MyVoteRound[]; isError?: boolean; onRetry?: () => void }) {
  // V8: a failed load must be visible, not a silent empty section.
  if (isError) {
    return (
      <div className="card" style={{ marginBottom: 12, borderColor: 'var(--bad)' }}>
        <span className="small" style={{ color: 'var(--bad)' }}>🗳 定标投票加载失败 / could not load your votes.</span>{' '}
        <button className="small" onClick={onRetry}>重试 / Retry</button>
      </div>
    );
  }
  if (rounds.length === 0) return null;
  const owed = pendingVoteCount(rounds);
  const nominating = rounds.filter((r) => r.kind === 'nominate').length;
  return (
    <div className="card tasks-votes" style={{ marginBottom: 12, borderColor: owed ? 'var(--warn)' : undefined, background: owed ? 'var(--warn-soft)' : undefined }}>
      <div className="row-wrap vote-row" style={{ gap: 8, alignItems: 'baseline' }}>
        <strong>🗳 定标投票 / Recommendation votes</strong>
        {owed > 0 && <span className="pill pill-warn small">{nominating > 0 ? `待推荐/待投 ${owed}` : `待投 ${owed}`}</span>}
        {rounds.length > owed && <span className="pill pill-good small">已完成 {rounds.length - owed}</span>}
        <span className="spacer" />
        <span className="muted small">票投齐了这个标才能继续 / the tender is blocked until every vote is in</span>
      </div>
      {rounds.map((r) => {
        const isNom = r.kind === 'nominate';
        const acted = r.voters.filter((v) => (isNom ? v.nominated_at : v.voted_at)).length;
        // 补充表决 (改改 fb_828ed0af): I already voted this round and owe a ballot only on what was ADDED
        // since — say so on the row, so a second notification does not read as "vote it all again".
        const inPlay = r.candidates.filter((c) => c.availability !== 'unavailable').length;
        const pending = r.my_pending_candidate_ids?.length ?? inPlay;
        const isTopUp = r.kind === 'vote' && pending > 0 && pending < inPlay;
        return (
          <div key={r.round.id} style={{ marginTop: 8, paddingTop: 6, borderTop: '1px solid var(--rule)' }}>
            <div className="row-wrap small vote-row" style={{ gap: 8, alignItems: 'baseline' }}>
              <strong>{r.package?.project_name ?? r.package?.name ?? '采购包'}</strong>
              {r.package?.project_name && <span className="muted">{r.package.name}</span>}
              {r.kind === 'result'
                ? <span className={`pill small ${VOTE_STATUS_LABEL.completed.pill}`}>{VOTE_STATUS_LABEL.completed.label}</span>
                : <span className={`pill small ${VOTE_PHASE_LABEL[r.round.phase].pill}`}>{VOTE_PHASE_LABEL[r.round.phase].label}</span>}
              {isTopUp && (
                <span className="pill pill-warn small" title={`本轮新增了候选，您只需对新增的 ${pending} 家补投一票 / candidates were added — only ${pending} need your ballot`}>
                  补充表决 {pending} / top-up
                </span>
              )}
              <span className="spacer" />
              <span className="muted">{isNom ? `已推荐 ${acted}/${r.voters.length}` : `已投 ${acted}/${r.voters.length}`} · 发起人 {r.round.created_by_name ?? '—'}</span>
            </div>
            {/* V6: WHO else is on this panel, and who is still holding it up — right where I act. */}
            <div style={{ marginTop: 2 }}><VoterChips voters={r.voters} phase={isNom ? 'nominating' : 'voting'} /></div>
            {r.round.note && <div className="small muted">{r.round.note}</div>}
            {r.kind === 'nominate' && <VoteNominationForm view={r} />}
            {r.kind === 'vote' && <VoteBallotForm view={r} />}
            {r.kind === 'result' && (
              <div style={{ marginTop: 4 }}>
                <div className="small muted">
                  ✓ 本轮已完成 —— 结果仅供查看。反对不会自动淘汰，由发起人或总经理定夺。
                  / Completed — read-only. An objection flags a candidate; it never eliminates one.
                </div>
                {r.candidates.map((c) => <CandidateRow key={c.id} v={r} c={c} />)}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

/** GM-only: match the staff roster to the platform user directory in one click. */
function SyncButton({ onSynced }: { onSynced: () => void }) {
  const [msg, setMsg] = useState<string | null>(null);
  const mut = useMutation({
    mutationFn: syncTaskAssignees,
    onSuccess: (r) => {
      setMsg(`已映射 ${r.matched} / ${r.matched + r.unmatched} 人${r.unmatched ? `（未匹配：${r.unmatched_list.map((u) => u.name).join('、')}）` : ''}`);
      onSynced();
    },
    onError: (e: any) => setMsg(e?.body?.error ?? e?.message ?? '同步失败'),
  });
  return (
    <span className="row-wrap small" style={{ gap: 8, alignItems: 'center' }}>
      <button disabled={mut.isPending} onClick={() => mut.mutate()} title="按姓名/工号匹配任务系统账号 / match roster to the task-system directory">
        {mut.isPending ? '同步中… / syncing…' : '🔄 同步账号映射 / Sync ids'}
      </button>
      {msg && <span className="muted">{msg}</span>}
    </span>
  );
}

function DelegateForm({ configured, onCreated }: { configured: boolean; onCreated: () => void }) {
  const [title, setTitle] = useState('');
  const [assignee, setAssignee] = useState<Assignee | null>(null);
  const [priority, setPriority] = useState('medium');
  const [due, setDue] = useState('');
  const [desc, setDesc] = useState('');
  const [err, setErr] = useState<string | null>(null);

  const mut = useMutation({
    mutationFn: () => createTask({
      title: title.trim(), assignee_user_id: assignee?.uuid, assignee_name: assignee?.name, priority,
      due_date: due || undefined, description: desc || undefined,
    }),
    onSuccess: () => { setTitle(''); setAssignee(null); setDue(''); setDesc(''); setErr(null); onCreated(); },
    onError: (e: any) => setErr(e?.body?.detail ?? e?.body?.error ?? e?.message ?? '失败'),
  });

  if (!configured) {
    return (
      <div className="card" style={{ borderColor: 'var(--warn)', background: 'var(--warn-soft)' }}>
        <span className="small">任务系统未配置 — 待管理员（邹兴兵）开通后即可派发任务。
          / Task API not configured yet; delegation activates once the admin enables it.</span>
      </div>
    );
  }

  return (
    <div className="card" style={{ marginBottom: 12 }}>
      <strong className="small">派发任务 / Delegate a task</strong>
      <div className="row-wrap delegate-fields" style={{ gap: 8, alignItems: 'flex-end', marginTop: 8 }}>
        <div className="field delegate-title" style={{ marginBottom: 0 }}>
          <label>标题 / Title</label>
          <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="例如：现场踏勘 / e.g. site visit" />
        </div>
        <div className="field delegate-assignee" style={{ marginBottom: 0 }}>
          <label>指派给 / Assign to</label>
          <AssigneePicker value={assignee} onChange={setAssignee} />
        </div>
        <div className="field" style={{ marginBottom: 0 }}>
          <label>优先级 / Priority</label>
          <select value={priority} onChange={(e) => setPriority(e.target.value)}>
            {Object.entries(TASK_PRIORITY_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </div>
        <div className="field" style={{ marginBottom: 0 }}>
          <label>截止 / Due</label>
          <input type="date" value={due} onChange={(e) => setDue(e.target.value)} />
        </div>
        <button className="btn-primary" disabled={mut.isPending || !title.trim() || !assignee} onClick={() => mut.mutate()}>
          {mut.isPending ? '派发中… / sending…' : '派发 / Delegate'}
        </button>
      </div>
      <div className="field" style={{ marginTop: 8, marginBottom: 0 }}>
        <label>说明 / Notes (optional)</label>
        <input value={desc} onChange={(e) => setDesc(e.target.value)} placeholder="补充说明 / extra detail" />
      </div>
      {err && <div className="small" style={{ color: 'var(--bad)', marginTop: 6 }}>{err}</div>}
    </div>
  );
}
